// v0.70 — Drop confirm page: mdrop.it/c/{id}
//
// Opens with NO login (pay-by-link pattern): the drop is a public read and the
// cached credential rebuilds the account silently. The card shows the
// recipient as the sender's own label + their profile, and "From: 🐙 …" so
// the sender can see which wallet will sign — `release` is sender-only on
// chain, so a mismatch offers "Use another wallet" instead of the slider.
// Slide → one Face ID → MDrop.release(id, claimerAddress).

import { encodeFunctionData, stringToHex } from "viem";
import { sponsorOnTouch, type PreparedOp, type WalletAdapter } from "../adapters/WalletAdapter";
import { readCredential } from "../credentialStore";
import {
  dropCancelled,
  dropRest,
  recordTx,
  dropReleased,
  dropUrl,
  getDrop,
  getDropConfig,
  getNameOf,
  resolveName,
  type DropView,
} from "../api";
import { canonicalizeCode } from "../codes";
import { emojiFor } from "../emoji";
import { ARROW_R } from "../icons";
import { LOGO_M } from "../logo";
import { playDone } from "../sound";
import { formatUsdShort } from "../money";
import { formatFx } from "../fx";
import { noteDropLabel } from "../nickname";
import { addRecent } from "../recentStore";
import { dropSharePayload, shareOrCopy } from "../share";
import { initSlideToConfirm } from "../slideToConfirm";
import { isPasskeyCancel, PASSKEY_CANCEL_LINE, recordError } from "../diag"; // v0.90.22
import { getHeld, isNewLabel, labelFor, removeHeld, secretFor } from "../heldStore";
import { cachedAddress, connectHere, escapeHtml } from "./dropShared";

// v0.80.31 — a Drop's amount in its own currency (USD until the row says otherwise).
const fmtDrop = (d: { amount: string; currency?: string }) =>
  d.currency && d.currency !== "USD" ? formatFx(d.currency, Number(d.amount)) : formatUsdShort(Number(d.amount));

const MDROP_RELEASE_ABI = [
  {
    type: "function",
    name: "release",
    stateMutability: "nonpayable",
    inputs: [
      { name: "id", type: "bytes32" },
      { name: "to", type: "address" },
    ],
    outputs: [],
  },
] as const;

interface Deps {
  adapter: WalletAdapter;
  dropId: string;
  /** v0.90.21 — /c/{id}/{code}: the wallet code "Tell Bob" put in the link. */
  by?: string;
  onGoHome: () => void;
  onEnterAs: (address: string) => void;
}

export function renderDropConfirmView(root: HTMLElement, { adapter, dropId, by, onGoHome, onEnterAs }: Deps) {
  const id = dropId.toUpperCase().replace(/[^0-9A-Z]/g, "");
  const idBytes32 = stringToHex(id, { size: 32 });

  root.innerHTML = `
    <main class="screen pay">
      <div class="brand small">${LOGO_M}</div>
      <p class="loading" id="loading">Opening your Drop…</p>
    </main>`;

  getDrop(id).then(renderByStatus, renderInvalid);

  // "Lucy (Lucy Parker) 🐙" — the sender's own label, the payee's profile name in
  // brackets, their emoji. No wallet codes anywhere.
  const toLine = (d: DropView) =>
    `<span class="cap">${escapeHtml(d.label)}</span> (${d.claimerName ? `${escapeHtml(d.claimerName)} ` : ""}${emojiFor(d.claimerCode, d.claimerEmoji)})`;
  const fromLine = (d: DropView) =>
    `${emojiFor(d.senderCode, d.senderEmoji)} ${escapeHtml(d.senderName ?? "your wallet")}`;

  function renderInvalid() {
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand">${LOGO_M}</div>
        <h1>This link isn't valid</h1>
        <p class="sub">The Drop may have been removed, or the link was cut short.</p>
        <button id="home" class="btn ghost">Go to M</button>
      </main>`;
    root.querySelector("#home")!.addEventListener("click", onGoHome);
  }

  function renderByStatus(d: DropView) {
    if (d.bearer) return void renderRest(d); // v0.90.21
    if (d.status === "claimed") return renderRelease(d);
    if (d.status === "open") return renderWaiting(d);
    // Already released / cancelled / refunded: nothing to do here — open the wallet.
    void enter();
  }

  async function enter() {
    try {
      const { address } = await connectHere(adapter, false);
      onEnterAs(address);
    } catch {
      onGoHome();
    }
  }

  // Not claimed yet: the recipient hasn't joined. Re-share the drop link.
  function renderWaiting(d: DropView) {
    const url = dropUrl(location.origin, d.id);
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand small">${LOGO_M}</div>
        <h1><span class="cap">${escapeHtml(d.label)}</span> hasn't claimed yet.</h1>
        <p class="sub">${escapeHtml(fmtDrop(d))} is waiting. Send the Drop link again.</p>
        <div class="req-link">${escapeHtml(url.replace(/^https?:\/\//, ""))}</div>
        <div class="actions">
          <button id="share" class="btn primary block">Share the link</button>
          <button id="home" class="linkish small" type="button">Open my wallet</button>
        </div>
        <p id="status" class="status" role="status" aria-live="polite"></p>
      </main>`;
    root.querySelector("#share")!.addEventListener("click", () =>
      void shareOrCopy(dropSharePayload(fmtDrop(d), url)),
    );
    root.querySelector("#home")!.addEventListener("click", () => void enter());
  }

  // The wallet that will sign must be the one that funded the drop.
  async function isCreator(address: string, d: DropView): Promise<boolean> {
    const want = d.senderCode;
    if (!want) return true; // nothing to compare against — let the chain decide
    try {
      const n = await getNameOf(address);
      return !!n?.display && canonicalizeCode(n.display) === canonicalizeCode(want);
    } catch {
      return true;
    }
  }

  async function renderRelease(d: DropView) {
    root.innerHTML = `
      <main class="screen pay drop-release">
        <div class="brand small">${LOGO_M}</div>
        <div class="paycard">
          <div class="pc-title">Releasing</div>
          <div class="pc-row"><span class="pc-k">Amount:</span><b class="pc-amt">${escapeHtml(fmtDrop(d))}</b></div>
          <div class="pc-row"><span class="pc-k">To:</span><span class="pc-v">${toLine(d)}</span></div>
        </div>
        <div id="act"></div>
        <p id="status" class="status" role="status" aria-live="polite"></p>
        <div id="dropOverlay" class="drop-overlay" hidden></div>
      </main>`;
    const act = root.querySelector<HTMLElement>("#act")!;
    const statusEl = root.querySelector<HTMLElement>("#status")!;

    // Pre-check the cached wallet against the creator BEFORE showing the slider.
    const cached = cachedAddress();
    const ok = cached ? await isCreator(cached, d) : true;
    if (!ok) return renderOtherWallet();

    act.innerHTML = `
      <div class="slide" id="relSlide" role="button" aria-label="Release">
        <div class="fill"></div>
        <div class="lbl">Release</div>
        <div class="ghost">${ARROW_R}</div>
        <div class="thumb">${ARROW_R}</div>
      </div>`;
    const slider = initSlideToConfirm(root.querySelector<HTMLElement>("#relSlide")!, {
      onConfirm: () => void doRelease(false),
    });

    function renderOtherWallet() {
      // Only shown when the cached wallet isn't the one that funded the Drop.
      act.innerHTML = `
        <p class="sub">This Drop was sent from ${fromLine(d)}. Log in with that wallet to release it.</p>
        <button id="other" class="btn primary block" type="button">Use another wallet</button>`;
      root.querySelector("#other")!.addEventListener("click", () => void doRelease(true));
    }

    const overlay = root.querySelector<HTMLElement>("#dropOverlay")!;
    function overlaySpinner() {
      overlay.innerHTML = `
        <div class="do-body">
          <div class="spinner big"></div>
          <div class="do-h">Releasing ${escapeHtml(fmtDrop(d))}…</div>
          <div class="do-s">Confirming on Circle Arc</div>
        </div>`;
      overlay.removeAttribute("hidden");
    }
    function overlaySuccess(senderAddress: string) {
      playDone(); // 1.5.10 — M's "done": a completion has landed
      overlay.innerHTML = `
        <div class="do-stack">
          <div class="brand small">${LOGO_M}</div>
          <div class="do-card">
            <div class="tickbig">✓</div>
            <div class="do-h"><span class="amt">${escapeHtml(fmtDrop(d))}</span></div>
            <div class="do-name small">sent to ${toLine(d)}</div>
            <div class="do-s">Next time,<br/>to send money to the same person again<br/>just type "${escapeHtml(d.label)}".</div>
            <button id="doHome" class="btn ghost block" style="margin-top:12px">Open my wallet</button>
          </div>
        </div>`;
      overlay.removeAttribute("hidden");
      overlay.querySelector("#doHome")!.addEventListener("click", () => onEnterAs(senderAddress));
    }
    function overlayError(msg: string) {
      overlay.innerHTML = `
        <button id="doClose" class="do-close" type="button" aria-label="Close">✕</button>
        <div class="do-body">
          <div class="do-h">Release didn't go through</div>
          <div class="do-s">${escapeHtml(msg)}</div>
          <button id="doBack" class="btn ghost" type="button" style="margin-top:14px">Back</button>
        </div>`;
      overlay.removeAttribute("hidden");
      const back = () => {
        overlay.setAttribute("hidden", "");
        overlay.innerHTML = "";
        slider?.reset();
      };
      overlay.querySelector("#doClose")!.addEventListener("click", back);
      overlay.querySelector("#doBack")!.addEventListener("click", back);
    }

    async function doRelease(usePicker: boolean) {
      statusEl.textContent = usePicker
        ? "Choose a wallet, then confirm — waiting for Face ID…"
        : "Confirm — waiting for Face ID…";
      let sender: { address: string };
      try {
        sender = await connectHere(adapter, usePicker);
      } catch (e) {
        console.warn("[M] release: connect failed", e);
        statusEl.textContent = "Not confirmed. Tap to try again.";
        slider?.reset();
        return;
      }
      if (!(await isCreator(sender.address, d))) {
        statusEl.textContent = "";
        slider?.reset();
        return renderOtherWallet();
      }
      if (!d.claimerCode) {
        statusEl.textContent = "The claim didn't carry a wallet. Ask them to open the link again.";
        slider?.reset();
        return;
      }
      overlaySpinner();
      try {
        const cfg = await getDropConfig();
        // v0.80.31 — release against the escrow that holds THIS drop's currency.
        const esc = cfg.contracts?.[d.currency ?? "USD"]?.contract ?? cfg.contract!;
        const to = (await resolveName(canonicalizeCode(d.claimerCode))).address;
        // v0.90.26 — success at acceptance; /drop/:id/released runs when the receipt lands.
        const sub = await adapter.submit([
          {
            to: esc as `0x${string}`,
            data: encodeFunctionData({
              abi: MDROP_RELEASE_ABI,
              functionName: "release",
              args: [idBytes32, to as `0x${string}`],
            }),
          },
        ]);
        const base = { address: sender.address, userOpHash: sub.userOpHash, direction: "send" as const, amount: d.amount, counterparty: to, currency: d.currency ?? "USD", kind: "drop_release" as const, dropId: d.id };
        recordTx(base).catch(() => {});
        addRecent({ address: to, display: d.claimerCode });
        noteDropLabel(to, d.label); // v0.70.12 — your name for them, on this device only
        statusEl.textContent = "";
        overlaySuccess(sender.address);
        void sub.settled
          .then(async ({ txHash, success }) => {
            recordTx({ ...base, txHash, success }).catch(() => {});
            if (!success) { recordError("drop/release/settle", new Error("userop_reverted " + txHash)); return; }
            await dropReleased(d.id, txHash).catch((e) => console.warn("[M] drop/released failed:", e));
          })
          .catch((e) => recordError("drop/release/settle", e));
      } catch (e) {
        console.error(e);
        recordError("drop/release", e); // v0.90.22
        if (isPasskeyCancel(e)) { // v0.90.22 — a cancel re-arms the slide, no card
          overlay.setAttribute("hidden", "");
          overlay.innerHTML = "";
          statusEl.textContent = PASSKEY_CANCEL_LINE;
          slider?.reset();
          return;
        }
        overlayError("Nothing moved. Please try again.");
      }
    }
  }

  // --- v0.90.21 — the rest of a first payment -------------------------------
  // Opened from Lucy's "Tell Bob" link, the To-field chip or the Pending row.
  // The card never prompts on its own: it states what's held, asks Bob to
  // continue only if Lucy has told him the first part arrived, and shows the
  // claim passively. The name binds here, at Bob's slide.
  async function renderRest(d: DropView) {
    const held = getHeld(d.id);
    // v1.0.0 — a Drop sent to "Someone new" has no name of the sender's: the
    // recipient's own profile name stands in once they've claimed, and it is
    // never saved as a nickname (their own name shows anyway).
    const rawLabel = held?.label ?? labelFor(d.id) ?? d.label ?? "them";
    const unnamed = isNewLabel(rawLabel);
    const label = unnamed ? d.claimerName || "the recipient" : rawLabel;
    const L = unnamed && !d.claimerName ? "the recipient" : `<span class="cap">${escapeHtml(label)}</span>`;
    const Ls = unnamed && !d.claimerName ? "The recipient" : L; // at the start of a sentence
    const firstFig = fmtDrop(d);
    const claimer = d.claimerCode
      ? `${d.claimerName && !unnamed ? `${escapeHtml(d.claimerName)} ` : ""}${emojiFor(d.claimerCode, d.claimerEmoji)}`
      : "";
    const toLineR = `${L}${claimer ? ` (${claimer})` : ""}`;

    // Nothing held on this device.
    if (!held) {
      if (d.restStatus === "sent" || !d.rest) {
        // Unsplit (or already done): the only thing left is saving the name.
        if (d.status === "released" && d.claimerCode && labelFor(d.id) && unnamed) {
          // v1.0.0 — nothing to save as: they join the list under their own name.
          try {
            addRecent({ address: (await resolveName(canonicalizeCode(d.claimerCode))).address, display: d.claimerCode });
          } catch {
            /* offline — cosmetic */
          }
          return void enter();
        }
        if (d.status === "released" && d.claimerCode && labelFor(d.id)) {
          root.innerHTML = `
            <main class="screen pay center">
              <div class="brand small">${LOGO_M}</div>
              <div class="tickbig">✓</div>
              <h1>${toLineR} has your ${escapeHtml(firstFig)}.</h1>
              <p class="sub">Save them to your contacts as “${escapeHtml(label)}” — next time it's one step.</p>
              <div class="actions">
                <button id="save" class="btn primary block">Save ${L}</button>
                <button id="home" class="linkish small" type="button">Open my wallet</button>
              </div>
            </main>`;
          root.querySelector("#save")!.addEventListener("click", async () => {
            try {
              const to = (await resolveName(canonicalizeCode(d.claimerCode!))).address;
              noteDropLabel(to, label);
              addRecent({ address: to, display: d.claimerCode! });
            } catch {
              /* offline — they can be saved from Activity later */
            }
            void enter();
          });
          root.querySelector("#home")!.addEventListener("click", () => void enter());
          return;
        }
        return void enter();
      }
      root.innerHTML = `
        <main class="screen pay center">
          <div class="brand small">${LOGO_M}</div>
          <h1>Open this on the phone you sent the ${escapeHtml(firstFig)} from.</h1>
          <p class="sub">The rest is held there, not in M. Nothing has moved.</p>
          <button id="home" class="btn ghost block">Open my wallet</button>
        </main>`;
      root.querySelector("#home")!.addEventListener("click", () => void enter());
      return;
    }

    const restFig = held.currency === "USD" ? formatUsdShort(Number(held.rest)) : formatFx(held.currency, Number(held.rest));
    const claimedOk = d.status === "released" && !!d.claimerCode;
    const mismatch = claimedOk && !!by && canonicalizeCode(by) !== canonicalizeCode(d.claimerCode!);
    const gone = d.status === "cancelled" || d.status === "refunded";
    const status = gone
      ? `<p class="rest-status bad">The ${escapeHtml(firstFig)} came back to you, so ${L} never got it.</p>`
      : mismatch
        ? `<p class="rest-status bad">The ${escapeHtml(firstFig)} was claimed by a different wallet from the one in this link. Don't send the rest until you've spoken to ${L}.</p>`
        : !claimedOk && d.status !== "open"
          ? `<p class="rest-status">Checking the ${escapeHtml(firstFig)}…</p>`
          : ""; // v1.0.2 — no "claimed by …" line: the card's To says who
    const canPay = claimedOk && !mismatch;
    const waiting = d.status === "open";

    // v1.0.2 — the card is what you're about to send, so it shows only once
    // the first part has been claimed (the name is known then). Before that:
    // who hasn't claimed yet, the link again, and a way to call it all off.
    root.innerHTML = `
      <main class="screen pay drop-release${waiting ? " rest-waiting" : ""}">
        <div class="brand small">${LOGO_M}</div>
        ${waiting ? `
        <div class="do-card rest-wait">
          <div class="do-h">${unnamed && !d.claimerName ? "They haven't" : `${Ls} hasn't`} claimed ${escapeHtml(firstFig)} yet.</div>
          <div class="do-s">Your remaining ${escapeHtml(restFig)} stays in your wallet until ${unnamed && !d.claimerName ? "they confirm" : `${L} confirms`} receipt.</div>
          <div class="actions in-card">
            <button id="reshare" class="btn primary block" type="button">Share the ${escapeHtml(firstFig)} link again</button>
            <button id="drop" class="linkish small muted" type="button">Cancel this transaction</button>
          </div>
        </div>` : gone ? "" : `
        <div class="paycard">
          <div class="pc-title">Sending</div>
          <div class="pc-row"><span class="pc-k">Amount:</span><b class="pc-amt">${escapeHtml(restFig)}</b></div>
          <div class="pc-row"><span class="pc-k">To:</span><span class="pc-v">${toLineR}</span></div>
          ${held.ref ? `<div class="pc-row"><span class="pc-k">Ref:</span><span class="pc-v">${escapeHtml(held.ref)}</span></div>` : ""}
        </div>
        <p class="rest-check">Only continue if<br/>${L} confirmed<br/>that ${escapeHtml(firstFig)} arrived.</p>`}
        ${status}
        <div id="act"></div>
        <p id="status" class="status" role="status" aria-live="polite"></p>
        ${waiting ? "" : `<div class="actions">
          <button id="drop" class="linkish small muted" type="button">${gone ? "Remove" : "Cancel this transaction"}</button>
        </div>`}
        <div id="dropOverlay" class="drop-overlay" hidden></div>
      </main>`;
    const act = root.querySelector<HTMLElement>("#act")!;
    const statusEl = root.querySelector<HTMLElement>("#status")!;
    const overlay = root.querySelector<HTMLElement>("#dropOverlay")!;

    root.querySelector("#reshare")?.addEventListener("click", () => {
      const secret = secretFor(d.id);
      if (!secret) return;
      void shareOrCopy(dropSharePayload(firstFig, dropUrl(location.origin, d.id, secret)));
    });
    // v1.0.2 — "Cancel this transaction". Unclaimed: the first part comes back
    // from the contract (one Face ID, the same call as Balance → Pending →
    // Cancel) and the rest is let go. Claimed: the first part is theirs — the
    // rest is simply not sent. Nothing held here moves by itself either way.
    const dropBtn = root.querySelector<HTMLButtonElement>("#drop")!;
    dropBtn.addEventListener("click", async () => {
      if (!waiting) {
        removeHeld(d.id);
        const me = cachedAddress();
        if (me) await dropRest(d.id, { senderAddress: me, dropped: true }).catch(() => {});
        return void enter();
      }
      dropBtn.disabled = true;
      statusEl.textContent = "Confirm — waiting for Face ID…";
      try {
        const sender = await connectHere(adapter, false);
        if (!(await isCreator(sender.address, d))) {
          statusEl.textContent = `This was sent from ${fromLine(d)}. Log in with that wallet to cancel it.`;
          dropBtn.disabled = false;
          return;
        }
        const cfg = await getDropConfig();
        const contract = cfg.bearer?.[d.currency ?? "USD"]?.contract;
        if (!contract) throw new Error("no_bearer_contract");
        const { txHash } = await adapter.execute([{
          to: contract as `0x${string}`,
          data: encodeFunctionData({
            abi: [{ type: "function", name: "cancel", stateMutability: "nonpayable", inputs: [{ name: "id", type: "bytes32" }], outputs: [] }] as const,
            functionName: "cancel",
            args: [stringToHex(d.id, { size: 32 })],
          }),
        }]);
        await dropCancelled(d.id, txHash).catch(() => {});
        removeHeld(d.id);
        await dropRest(d.id, { senderAddress: sender.address, dropped: true }).catch(() => {});
        root.innerHTML = `
          <main class="screen pay drop-release">
            <div class="brand small">${LOGO_M}</div>
            <div class="do-card rest-wait">
              <div class="tickbig">✓</div>
              <div class="do-h">Cancelled.</div>
              <div class="do-s">The ${escapeHtml(firstFig)} is back in your wallet, and nothing else was sent.</div>
              <button id="home" class="btn ghost block" style="margin-top:12px">Open my wallet</button>
            </div>
          </main>`;
        root.querySelector("#home")!.addEventListener("click", () => onEnterAs(sender.address));
      } catch (e) {
        recordError("drop/cancel", e);
        dropBtn.disabled = false;
        // A claim that landed first makes the cancel revert: say so plainly.
        statusEl.textContent = isPasskeyCancel(e) ? PASSKEY_CANCEL_LINE : "Couldn't cancel — it may have just been claimed. Open this page again to check.";
      }
    });

    if (!canPay) return;
    act.innerHTML = `
      <div class="slide" id="restSlide" role="button" aria-label="Pay ${escapeHtml(label)}">
        <div class="fill"></div>
        <div class="lbl"><span>Pay <span class="cap">${escapeHtml(label.length > 18 ? `${label.slice(0, 17).trimEnd()}…` : label)}</span></span></div>
        <div class="ghost">${ARROW_R}</div>
        <div class="thumb">${ARROW_R}</div>
      </div>`;
    const slider = initSlideToConfirm(root.querySelector<HTMLElement>("#restSlide")!, {
      onConfirm: () => void doRest(),
      // v2.0 — the sponsor's signature for the prepared rest, asked for when the finger lands (Circle's stub only before).
      onTouch: () => { if (restPrep) sponsorOnTouch(adapter, restPrep.then((p) => p?.prepared ?? null)); },
    });

    // v1.0.7 — as fast as a Send: while this card is read, the wallet cached
    // here connects (no Face ID), the sender check and the recipient lookup
    // run, and the transfer is PREPARED. The slide only signs.
    type RestPrep = { sender: string; to: string; creator: boolean; prepared: PreparedOp | null };
    const cred = readCredential();
    const restPrep: Promise<RestPrep | null> | null = cred
      ? (async (): Promise<RestPrep> => {
          const { address: sender } = await adapter.connectWithCredential(cred);
          const [creator, to] = await Promise.all([
            isCreator(sender, d),
            resolveName(canonicalizeCode(d.claimerCode!)).then((r) => r.address),
          ]);
          const prepared = creator ? await adapter.prepareTransfer(to, held!.rest, held!.currency).catch(() => null) : null;
          return { sender, to, creator, prepared };
        })().catch((e) => {
          console.warn("[M] rest prep failed", e);
          return null;
        })
      : null;

    async function doRest() {
      statusEl.textContent = "Confirm — waiting for Face ID…";
      const prep = restPrep ? await restPrep : null;
      let sender: { address: string };
      try {
        sender = prep ? { address: prep.sender } : await connectHere(adapter, false);
      } catch {
        statusEl.textContent = "Not confirmed. Tap to try again.";
        slider.reset();
        return;
      }
      if (!(prep ? prep.creator : await isCreator(sender.address, d))) {
        statusEl.textContent = `This was sent from ${fromLine(d)}. Log in with that wallet to send the rest.`;
        slider.reset();
        return;
      }
      statusEl.textContent = "";
      overlay.innerHTML = `
        <div class="do-body">
          <div class="spinner big"></div>
          <div class="do-h">Sending ${escapeHtml(restFig)}…</div>
          <div class="do-s">Confirming on Circle Arc</div>
        </div>`;
      overlay.removeAttribute("hidden");
      try {
        const to = prep?.to ?? (await resolveName(canonicalizeCode(d.claimerCode!))).address;
        // v0.90.26 — success at acceptance; the rest's hash reaches the Worker
        // from this tab's receipt or from the chain (pending.ts, kind drop_rest).
        let sub = null as Awaited<ReturnType<typeof adapter.submitTransfer>> | null;
        if (prep?.prepared) {
          try {
            sub = await adapter.sendPrepared(prep.prepared); // v1.0.7 — Face ID at once
          } catch (e) {
            if (isPasskeyCancel(e)) throw e;
            if (!(e instanceof Error && e.message === "prepared_stale")) recordError("drop/rest/prepared", e);
          }
        }
        sub ??= await adapter.submitTransfer(to, held!.rest, held!.currency);
        removeHeld(d.id);
        if (!unnamed) noteDropLabel(to, label); // the name binds here, at the sender's slide
        addRecent({ address: to, display: d.claimerCode! });
        const base = { address: sender.address, userOpHash: sub.userOpHash, direction: "send" as const, amount: held!.rest, counterparty: to, currency: held!.currency, kind: "drop_rest" as const, dropId: d.id, ref: held!.ref || undefined }; // v1.0.4 — the Drop's reference rides the rest
        recordTx(base).catch(() => {});
        void sub.settled
          .then(async ({ txHash, success }) => {
            recordTx({ ...base, txHash, success }).catch(() => {});
            if (!success) { recordError("drop/rest/settle", new Error("userop_reverted " + txHash)); return; }
            await dropRest(d.id, { senderAddress: sender.address, txHash }).catch(() => {});
          })
          .catch((e) => recordError("drop/rest/settle", e));
        overlay.innerHTML = `
          <div class="do-stack">
            <div class="brand small">${LOGO_M}</div>
            <div class="do-card">
              <div class="tickbig">✓</div>
              <div class="do-h succ"><span class="amt">${escapeHtml(restFig)}</span> was sent to</div>
              <div class="do-name succ">${toLineR}</div>
              <div class="do-s">${Ls} is in your contacts now — next time it's one step.</div>
              <div class="do-s muted">Receipt in Recent transactions.</div>
              <button id="doHome" class="btn ghost block" style="margin-top:12px">Open my wallet</button>
            </div>
          </div>`;
        overlay.querySelector("#doHome")!.addEventListener("click", () => onEnterAs(sender.address));
      } catch (e) {
        console.error(e);
        recordError("drop/rest", e); // v0.90.22
        if (isPasskeyCancel(e)) { // v0.90.22 — a cancel re-arms the slide, no card
          overlay.setAttribute("hidden", "");
          overlay.innerHTML = "";
          statusEl.textContent = PASSKEY_CANCEL_LINE;
          slider.reset();
          return;
        }
        overlay.innerHTML = `
          <button id="doClose" class="do-close" type="button" aria-label="Close">✕</button>
          <div class="do-body">
            <div class="do-h">Didn't go through</div>
            <div class="do-s">Nothing moved. Check the balance covers ${escapeHtml(restFig)} and try again.</div>
            <button id="doBack" class="btn ghost" type="button" style="margin-top:14px">Back</button>
          </div>`;
        const back = () => {
          overlay.setAttribute("hidden", "");
          overlay.innerHTML = "";
          slider.reset();
        };
        overlay.querySelector("#doClose")!.addEventListener("click", back);
        overlay.querySelector("#doBack")!.addEventListener("click", back);
      }
    }
  }
}
